import {FormControl, FormField, Separator, TextInput} from 'platform/components';
import {Grid, Heading, VStack} from 'platform/foundation';

import {defaultTo} from 'ramda';
import {isNilOrEmpty, isTrue} from 'ramda-adjunct';

import {useGetManufacturersQuery} from '@dms/api/core';
import {useGetOperationItemsQuery} from '@dms/api/finance';
import {
  GetWarehousesResponse,
  useGetMarketingCodesQuery,
  useGetSupplierDiscountGroupsQuery,
  useGetSuppliersQuery,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {
  getOptionsFromManufacturers,
  getOptionsFromSuppliers,
  getOptionsFromWarehouses,
  useGetVatRatesOptions,
  usePermissions,
} from '@dms/shared';
import {ArticleCreateTreeFolderPath as TreeFolderPath} from '@dms/shared-aftersales';
import {
  getMatchingManufacturers,
  getOptionsFromMarketingCodes,
  getOptionsFromSupplierDiscountGroups,
} from '@dms/warehouse/shared';

import {Nullish} from 'shared/web';

import {ArticleDetailForm} from '../types/ArticleDetailFormSchema';

interface GeneralInformationTabBasicInformationFormProps {
  articleId: string;
  control: FormControl<ArticleDetailForm>;
  manufacturerId: string;
  warehouseNumber: string | Nullish;
  warehouses: GetWarehousesResponse;
  supplierId: string | Nullish;
  authorizationProfileId: string | Nullish;
  leafId?: string | null;
  isRemovable: boolean;
  leafIdChange: (leafId?: string) => void;
}

export function GeneralInformationTabBasicInformationForm(
  props: GeneralInformationTabBasicInformationFormProps
) {
  const suppliersQuery = useGetSuppliersQuery();

  const marketingCodesQuery = useGetMarketingCodesQuery();

  const supplierDiscountGroupsQuery = useGetSupplierDiscountGroupsQuery(
    {
      supplierId: props.supplierId as string,
      authorizationProfileId: props.authorizationProfileId as string,
    },
    {skip: isNilOrEmpty(props.supplierId) || isNilOrEmpty(props.authorizationProfileId)}
  );

  const manufacturersQuery = useGetManufacturersQuery();

  const [articleEdit] = usePermissions({
    permissionKeys: ['articleEdit'],
  });

  const [vatOptions] = useGetVatRatesOptions({fullLabel: true});

  const {data: operationItems, isLoading: isOperationItemsLoading} = useGetOperationItemsQuery({
    application: 'workshop',
    itemGroup: 'material',
  });

  const matchingManufacturers = getMatchingManufacturers(
    manufacturersQuery.data,
    props.manufacturerId
  );

  const isManufacturerEditable = isTrue(props.isRemovable);

  const operationItemOptions =
    operationItems?.map((item) => ({
      label: item.itemCodeName,
      value: item.operationId,
    })) ?? [];

  return (
    <>
      <VStack spacing={3}>
        <Heading size={4}>{i18n.t('general.labels.basicInformation')}</Heading>
        <Grid columns={2}>
          <FormField
            isRequired
            control={props.control}
            type="text"
            name="generalInformation.basicInformation.name"
            label={i18n.t('general.labels.name')}
            data-testid={testIds.warehouse.articleDetailOverview('name')}
          />

          <FormField
            control={props.control}
            type="text"
            name="generalInformation.basicInformation.description"
            label={i18n.t('general.labels.description')}
            data-testid={testIds.warehouse.articleDetailOverview('description')}
          />
        </Grid>
        <Grid columns={4}>
          <FormField
            control={props.control}
            isRequired
            isDisabled={!isManufacturerEditable}
            type="text"
            name="generalInformation.basicInformation.manufacturerNumber"
            label={i18n.t('entity.warehouse.labels.manufacturerNumber')}
            data-testid={testIds.warehouse.articleDetailOverview('manufacturerNumber')}
          />
          <FormField
            isNotClearable
            isRequired
            isDisabled={!isManufacturerEditable}
            control={props.control}
            name="generalInformation.basicInformation.manufacturerId"
            type="choice"
            label={i18n.t('entity.warehouse.labels.manufacturer')}
            options={getOptionsFromManufacturers(matchingManufacturers)}
            data-testid={testIds.warehouse.articleDetailOverview('manufacturer')}
          />

          <FormField
            isNotClearable
            isRequired
            isLoading={suppliersQuery.isLoading}
            control={props.control}
            type="choice"
            name="generalInformation.basicInformation.supplierId"
            label={i18n.t('general.labels.supplier')}
            data-testid={testIds.warehouse.articleDetailOverview('supplier')}
            options={getOptionsFromSuppliers(suppliersQuery.data)}
          />

          <FormField
            isDisabled
            isLoading={supplierDiscountGroupsQuery.isLoading}
            control={props.control}
            type="choice"
            name="generalInformation.basicInformation.supplierDiscountGroupId"
            options={getOptionsFromSupplierDiscountGroups(supplierDiscountGroupsQuery.data)}
            label={i18n.t('entity.warehouse.labels.supplierDiscountGroup')}
            labelActions={[
              {
                title: i18n.t('general.labels.settings'),
                leftIcon: 'action/open_in_new',
                href: settingsRoutes.warehousesSupplierDiscountGroups,
                target: '_blank',
              },
            ]}
            data-testid={testIds.warehouse.articleDetailOverview('rabatGroup')}
          />
        </Grid>
        <Grid columns={4}>
          <FormField
            isRequired
            isDisabled
            control={props.control}
            type="choice"
            options={getOptionsFromWarehouses(props.warehouses)}
            name="generalInformation.basicInformation.warehouseId"
            label={i18n.t('entity.warehouse.labels.warehouse')}
            isNotClearable
            data-testid={testIds.warehouse.articleDetailOverview('warehouseId')}
          />
          <TextInput
            value={defaultTo(null, props.warehouseNumber)}
            label={i18n.t('entity.warehouse.labels.warehouseNumber')}
            isDisabled
            data-testid={testIds.warehouse.articleDetailOverview('warehouseNumber')}
          />

          <FormField
            control={props.control}
            type="text"
            name="generalInformation.basicInformation.storageLocation"
            label={i18n.t('general.warehouse.labels.storageLocation')}
            data-testid={testIds.warehouse.articleDetailOverview('storageLocation')}
          />
        </Grid>
        <TreeFolderPath
          leafId={props.leafId}
          onChange={(activeFolder) => props.leafIdChange(activeFolder?.id)}
          isDisabled={!articleEdit}
          data-testid={testIds.warehouse.articleDetailOverview('treeFolderPath')}
        />
      </VStack>
      <Separator />
      <VStack spacing={3}>
        <Grid columns={4}>
          <FormField
            isLoading={marketingCodesQuery.isLoading}
            control={props.control}
            type="choice"
            name="generalInformation.basicInformation.marketingCode"
            options={getOptionsFromMarketingCodes(marketingCodesQuery.data)}
            label={i18n.t('general.labels.marketingCode')}
            labelActions={[
              {
                title: i18n.t('general.labels.settings'),
                leftIcon: 'action/open_in_new',
                href: settingsRoutes.warehousesMarketingCode,
                target: '_blank',
              },
            ]}
            data-testid={testIds.warehouse.articleDetailOverview('marketingCode')}
          />
          <FormField
            menuInPortal
            control={props.control}
            name="generalInformation.basicInformation.workItemGroupId"
            type="choice"
            label={i18n.t('entity.addWork.labels.workItemGroup')}
            isLoading={isOperationItemsLoading}
            options={operationItemOptions}
          />
          <FormField
            isRequired
            control={props.control}
            type="choice"
            options={vatOptions}
            name="generalInformation.basicInformation.vatType"
            label={i18n.t('general.labels.vat')}
            isNotClearable
            data-testid={testIds.warehouse.articleDetailOverview('rates')}
          />
        </Grid>
      </VStack>
      <Separator />
    </>
  );
}
